import * as chrono from 'chrono-node'
import dayjs from 'dayjs'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { isValidBoolean } from '@/core/models/columns/boolean'

/**
 * Guesses the data type of a given string input.
 *
 * This function analyzes the input string and attempts to determine its data type.
 * It checks for various formats including:
 * - Boolean
 * - Number (including currency formats)
 * - Date
 * - Image URL
 * - General URL
 * - Text (default)
 *
 * The function uses a combination of regular expressions, built-in JavaScript
 * functions, and custom logic to make these determinations.
 *
 * @param {string} input - The string to be analyzed
 * @returns {string} - The guessed column type from ColumnTypes enum
 */
export function guessStringType(input: string): ColumnTypes {
  // Handle null/undefined/empty
  if (!input?.trim()) return ColumnTypes.Text

  const trimmedInput = input.trim()
  const lowerInput = trimmedInput.toLowerCase()

  // Check ISO-like dates early to avoid false positives with phone detection
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/
  const isoDateTimePattern = /^\d{4}-\d{2}-\d{2}[ tT]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/
  const ymdSlashPattern = /^\d{4}\/\d{2}\/\d{2}$/
  if (
    isoDatePattern.test(trimmedInput) ||
    isoDateTimePattern.test(trimmedInput) ||
    ymdSlashPattern.test(trimmedInput)
  ) {
    return ColumnTypes.DateTime
  }

  // Check if URL (require explicit protocol like http://, https://, etc.)
  const hasProtocol = /^[a-z][a-z0-9+\-.]*:\/\//i.test(trimmedInput)
  if (hasProtocol) {
    // Check if image URL
    const imagePattern = /\.(jpg|jpeg|png|gif|svg|webp)($|\?)/i
    if (imagePattern.test(trimmedInput) || trimmedInput.includes('unsplash.com')) {
      return ColumnTypes.Image
    }
    return ColumnTypes.URL
  }

  // Check if boolean (expanded)
  if (isValidBoolean(lowerInput)) {
    return ColumnTypes.Boolean
  }

  // Check if email
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (emailPattern.test(trimmedInput)) {
    return ColumnTypes.Email
  }

  // Check if number (simplified and more permissive)
  if (!isNaN(Number(trimmedInput)) || /^-?\d*\.?\d+e[+-]?\d+$/i.test(trimmedInput)) {
    return ColumnTypes.Number
  }

  // Check if date (using parseDateInput)
  const parsedDate = dayjs(chrono.parseDate(trimmedInput))
  if (parsedDate?.isValid()) {
    return ColumnTypes.DateTime
  }

  // Check if phone number (after date checks to avoid date false positives)
  const phonePattern = /^\+?[\d\s-().]{8,}$/
  if (phonePattern.test(trimmedInput.replace(/\s+/g, ''))) {
    return ColumnTypes.Phone
  }

  return ColumnTypes.Text
}
